import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { OverviewData } from '../hooks/useOverviewData';
import { computeInsights, Insight } from '../utils/insights';

interface Props {
    data: OverviewData | null;
}

/**
 * "Worth knowing" callout strip — top of the dashboard. Hides itself when
 * there's nothing material to surface, so it never adds noise on quiet days.
 */
const InsightsCard: React.FC<Props> = ({ data }) => {
    const insights = useMemo(() => computeInsights(data), [data]);
    if (insights.length === 0) return null;

    return (
        <section className="av2-insights" aria-label="Insights">
            <header className="av2-insights__header">
                <SparkleIcon />
                <h3 className="av2-insights__title">
                    <FormattedMessage
                        id="analyticsV2.insights.title"
                        defaultMessage="Worth knowing"
                    />
                </h3>
            </header>
            <ul className="av2-insights__list">
                {insights.map((insight, idx) => (
                    <InsightRow key={insight.id} insight={insight} index={idx} />
                ))}
            </ul>
        </section>
    );
};

const InsightRow: React.FC<{ insight: Insight; index: number }> = ({ insight, index }) => {
    const intl = useIntl();
    // Some insight values include nested i18n metadata (e.g. metric name) that
    // we resolve here before passing to FormattedMessage.
    const resolvedValues = useMemo(() => {
        if (!insight.titleValues) return undefined;
        const out: Record<string, React.ReactNode | string | number> = {};
        for (const [k, v] of Object.entries(insight.titleValues)) {
            if (v && typeof v === 'object' && '__i18n' in (v as any)) {
                const meta = (v as any).__i18n;
                out[k] = intl.formatMessage({ id: meta.id, defaultMessage: meta.default });
            } else {
                out[k] = v as any;
            }
        }
        return out;
    }, [insight.titleValues, intl]);

    return (
        <li
            className={`av2-insights__item av2-insights__item--${insight.severity}`}
            style={{ animationDelay: `${index * 60}ms` }}
        >
            <span className="av2-insights__dot" aria-hidden="true" />
            <div className="av2-insights__body">
                <div className="av2-insights__headline">
                    <FormattedMessage
                        id={insight.titleId}
                        defaultMessage={insight.titleDefault}
                        values={resolvedValues}
                    />
                </div>
                {insight.subId && (
                    <div className="av2-insights__sub">
                        <FormattedMessage
                            id={insight.subId}
                            defaultMessage={insight.subDefault || ''}
                            values={insight.subValues}
                        />
                    </div>
                )}
            </div>
        </li>
    );
};

const SparkleIcon: React.FC = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
        className="av2-insights__icon"
    >
        <path d="M12 2l1.5 5.5L19 9l-5.5 1.5L12 16l-1.5-5.5L5 9l5.5-1.5L12 2zm7 11l.9 3.1 3.1.9-3.1.9-.9 3.1-.9-3.1-3.1-.9 3.1-.9.9-3.1zM5 15l.6 2.1 2.1.6-2.1.6L5 20.4l-.6-2.1-2.1-.6 2.1-.6L5 15z" />
    </svg>
);

export default InsightsCard;
